import type {
  BatchGroupInfo,
  CheckLane,
  PipelineGraphSkeleton,
  PipelineLane,
  PipelineNodeType,
  PipelineSkeletonEdge,
  PipelineSkeletonNode,
} from "@/types/aicheck";

const NODE_IDS = {
  FETCH: "sys-fetch",
  CONSISTENCY_INDEPENDENT: "consistency-independent",
  COMPACTION: "sys-compaction",
  PROFILE: "sys-profile",
  CONSISTENCY_PROFILE: "consistency-profile",
} as const;

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function makeNode(
  id: string,
  nodeType: PipelineNodeType,
  label: string,
  lane: PipelineLane,
  extra?: Partial<PipelineSkeletonNode>,
): PipelineSkeletonNode {
  return {
    id,
    nodeType,
    label,
    lane,
    ...extra,
  };
}

function makeEdge(source: string, target: string): PipelineSkeletonEdge {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
  };
}

/**
 * グラフのノードに対応する「チェック名」だけを取り出す。
 *
 * `plannedChecks` には、実行の進捗表示用に **バッチのまとまりを表す名前**
 * （`"<グループ名> (batch 3)"`）も混ざる。これはノードではないので、
 * そのまま扱うと存在しない `ai-<表示名>` を指してしまう。
 *
 * **スケルトンを作る側と、状態を載せる側で同じ規則を使うこと。**
 * 片方だけが除外していたため、クライアントの state-mapper が該当ノードを
 * 見つけられず `[state-mapper] Unmapped check` を出し、そのノードは
 * 状態が載らないまま残っていた。
 */
export function graphCheckNames(
  plannedChecks: string[] | undefined,
  batchGroups: BatchGroupInfo[] | undefined,
): string[] {
  const batchDisplayPrefixes = (batchGroups ?? []).map(
    (bg) => `${bg.name} (batch `,
  );
  return [...new Set(plannedChecks ?? [])].filter(
    (name) => !batchDisplayPrefixes.some((pfx) => name.startsWith(pfx)),
  );
}

export function buildPipelineGraphSkeleton(
  checkLaneMap: Record<string, CheckLane> | undefined,
  plannedChecks: string[] | undefined,
  batchGroups?: BatchGroupInfo[],
): PipelineGraphSkeleton {
  const nodes: PipelineSkeletonNode[] = [];
  const edges: PipelineSkeletonEdge[] = [];

  const laneMap = checkLaneMap ?? {};
  const allChecks = graphCheckNames(plannedChecks, batchGroups);

  if (allChecks.length === 0) {
    nodes.push(makeNode(NODE_IDS.FETCH, "system", "Fetch Data", "pre-fork"));
    nodes.push(
      makeNode(
        NODE_IDS.CONSISTENCY_INDEPENDENT,
        "consistency",
        "Consistency Rules",
        "pre-fork",
        { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
      ),
    );
    edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.CONSISTENCY_INDEPENDENT));
    return { nodes: dedupeById(nodes), edges: dedupeById(edges) };
  }

  const checkToBatch = new Map<string, string>();
  const batchMap = new Map<string, BatchGroupInfo>();
  for (const bg of batchGroups ?? []) {
    batchMap.set(bg.id, bg);
    for (const name of bg.checks) {
      checkToBatch.set(name, bg.id);
    }
  }

  const independentChecks = allChecks.filter(
    (n) => laneMap[n] === "independent",
  );
  const dependentChecks = allChecks.filter((n) => laneMap[n] === "dependent");
  const afterAllChecks = allChecks.filter((n) => laneMap[n] === "afterAll");
  const unknownChecks = allChecks.filter((n) => !laneMap[n]);

  const laneAChecks = [...independentChecks, ...unknownChecks];
  const laneBChecks = dependentChecks;

  nodes.push(makeNode(NODE_IDS.FETCH, "system", "Fetch Data", "pre-fork"));
  nodes.push(
    makeNode(
      NODE_IDS.CONSISTENCY_INDEPENDENT,
      "consistency",
      "Consistency Rules",
      "pre-fork",
      { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
    ),
  );
  edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.CONSISTENCY_INDEPENDENT));

  function createCheckNodes(
    checks: string[],
    lane: PipelineLane,
    sourceNodeId: string,
  ): string[] {
    const nodeIds: string[] = [];
    const seenBatches = new Set<string>();

    for (const name of checks) {
      const batchId = checkToBatch.get(name);
      if (batchId && batchMap.has(batchId)) {
        if (seenBatches.has(batchId)) continue;
        seenBatches.add(batchId);
        const bg = batchMap.get(batchId);
        if (!bg) continue;
        const nodeId = `ai-batch-${batchId}`;
        const memberChecks = bg.checks.filter((c) => checks.includes(c));
        nodes.push(
          makeNode(nodeId, "ai-check", bg.name, lane, {
            checks: memberChecks,
            batchSize: memberChecks.length,
          }),
        );
        edges.push(makeEdge(sourceNodeId, nodeId));
        nodeIds.push(nodeId);
      } else {
        const nodeId = `ai-${name}`;
        nodes.push(
          makeNode(nodeId, "ai-check", name, lane, {
            checks: [name],
            batchSize: 1,
          }),
        );
        edges.push(makeEdge(sourceNodeId, nodeId));
        nodeIds.push(nodeId);
      }
    }
    return nodeIds;
  }

  const laneANodeIds = createCheckNodes(
    laneAChecks,
    "lane-a",
    NODE_IDS.CONSISTENCY_INDEPENDENT,
  );

  nodes.push(
    makeNode(NODE_IDS.COMPACTION, "system", "Contact Compaction", "lane-b"),
  );
  edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.COMPACTION));

  nodes.push(
    makeNode(NODE_IDS.PROFILE, "system", "Customer Profile", "lane-b"),
  );
  edges.push(makeEdge(NODE_IDS.COMPACTION, NODE_IDS.PROFILE));

  nodes.push(
    makeNode(
      NODE_IDS.CONSISTENCY_PROFILE,
      "consistency",
      "Profile Consistency",
      "lane-b",
      { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
    ),
  );
  edges.push(makeEdge(NODE_IDS.PROFILE, NODE_IDS.CONSISTENCY_PROFILE));

  const laneBNodeIds = createCheckNodes(
    laneBChecks,
    "lane-b",
    NODE_IDS.CONSISTENCY_PROFILE,
  );

  const terminalNodeIds: string[] = [];
  if (laneANodeIds.length > 0) terminalNodeIds.push(...laneANodeIds);
  if (laneBNodeIds.length > 0) terminalNodeIds.push(...laneBNodeIds);
  else terminalNodeIds.push(NODE_IDS.CONSISTENCY_PROFILE);

  if (afterAllChecks.length > 0) {
    const firstAfterAllId = `summary-${afterAllChecks[0]}`;
    nodes.push(
      makeNode(firstAfterAllId, "summary", afterAllChecks[0], "post-join", {
        checks: [afterAllChecks[0]],
        batchSize: 1,
      }),
    );
    for (const id of terminalNodeIds) {
      edges.push(makeEdge(id, firstAfterAllId));
    }

    let prevAfterAll = firstAfterAllId;
    for (let i = 1; i < afterAllChecks.length; i++) {
      const name = afterAllChecks[i];
      const nodeId = `summary-${name}`;
      nodes.push(
        makeNode(nodeId, "summary", name, "post-join", {
          checks: [name],
          batchSize: 1,
        }),
      );
      edges.push(makeEdge(prevAfterAll, nodeId));
      prevAfterAll = nodeId;
    }
  }

  return { nodes: dedupeById(nodes), edges: dedupeById(edges) };
}
